import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Switch,
  Stack,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import {
  INTEGRATION_CATALOG,
  CATEGORY_ORDER,
  type CatalogEntry,
  type CatalogField,
  type Capability,
  type IntegrationCategory,
} from '@replayops/contracts';
import { BrandIcon } from '../components/BrandIcon.js';
import { IntegrationField } from '../components/IntegrationField.js';
import { useMe } from '../api/hooks.js';
import { useActiveProject } from '../lib/activeProject.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

/**
 * The hub renders straight off the shared catalogue in @replayops/contracts,
 * which the API builds its providers from. One row per vendor, one place: a
 * card here and the client on the server can no longer disagree about a
 * vendor's fields, category or capabilities.
 *
 * It is imported from contracts, not from @replayops/integrations — that
 * package carries nodemailer and the vendor clients, and pulling it into the
 * browser bundle takes the whole app down with "Class extends value
 * undefined".
 */
type Field = CatalogField;
type Category = IntegrationCategory;

interface ProviderSpec {
  key: string;
  name: string;
  desc: string;
  category: Category;
  caps: Capability[];
  mechanism: CatalogEntry['mechanism'];
  apm: boolean;
  fields: Field[];
  docs?: string;
  /** The vendor speaks OAuth, so connecting can be a redirect. */
  oauth: boolean;
}

const PROVIDERS: ProviderSpec[] = INTEGRATION_CATALOG.map((e) => ({
  key: e.id,
  name: e.name,
  desc: e.blurb,
  category: e.category,
  caps: e.capabilities,
  mechanism: e.mechanism,
  apm: e.apm === true,
  fields: e.fields,
  docs: e.docs,
  oauth: e.oauth === true,
}));

/** How a mechanism reads on a card — the honest one-word version. */
/** A free profile name for "add another": default, then 2, 3, … */
function nextLabel(existing: { label: string }[]): string {
  if (!existing.some((e) => e.label === 'default')) return 'default';
  let n = 2;
  while (existing.some((e) => e.label === String(n))) n++;
  return String(n);
}

const MECHANISM_LABEL: Record<CatalogEntry['mechanism'], string> = {
  api: 'API',
  webhook: 'webhook',
  deeplink: 'link only',
  'email-intake': 'by email',
};


interface ConfiguredIntegration {
  id: string;
  provider: string;
  /** Profile name — one provider can be configured several times per project. */
  label: string;
  enabled: boolean;
  config: Record<string, unknown>;
  updatedAt: string;
}

/** Drops blank (optional) fields so they aren't persisted as empty strings. */
function prune(form: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim() !== ''));
}

/**
 * Why the far side did not store our address. Each of these leaves a working
 * integration — the Backend tab reads fine — and costs only the links that
 * would have gone the other way, so none of them is an error.
 */
const PAIRING_TROUBLE: Record<string, string> = {
  'already-configured':
    'Saved. CoverOps already points its session links somewhere else and kept that. Change it there if it is wrong.',
  unsupported: 'Saved. This CoverOps is too old to be told where we are; set the session link there by hand.',
  unreachable: 'Saved, but CoverOps could not be reached to be told where we are. Try the connection test.',
};

export function IntegrationsPage() {
  const all = useT();
  const c = all.integrations.hub;
  const common = all.common;
  const navigate = useNavigate();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const { projectId: activeProject, setProjectId, projects } = useActiveProject(orgId);
  const qc = useQueryClient();

  const [editing, setEditing] = useState<ProviderSpec | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ key: string; ok: boolean; msg: string } | null>(null);

  /**
   * Vendors this deployment registered an OAuth application with. Offering a
   * button for one it did not would send the operator to a vendor screen that
   * refuses them, with nothing on it explaining why.
   */
  const oauthAvailable =
    useQuery({
      enabled: !!orgId,
      queryKey: ['integration-oauth-available', orgId],
      queryFn: async () => (await api.get(`${base}/oauth/available`)).data.data as string[],
    }).data ?? [];

  const startConnect = async (provider: string) => {
    setConnecting(provider);
    try {
      const { authorizeUrl } = (await api.post(`${base}/${provider}/oauth/start`, {})).data as {
        authorizeUrl: string;
      };
      // A full navigation: the next page belongs to the vendor.
      window.location.assign(authorizeUrl);
    } catch (e) {
      setConnecting(null);
      setTestResult({
        key: provider,
        ok: false,
        msg:
          (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message ?? 'Could not start the connection.',
      });
    }
  };
  const [editingLabel, setEditingLabel] = useState('default');
  const [editingIsNew, setEditingIsNew] = useState(true);
  const [form, setForm] = useState<Record<string, string>>({});

  const base = `/v1/orgs/${orgId}/projects/${activeProject}/integrations`;

  const integrations = useQuery({
    enabled: !!orgId && !!activeProject,
    queryKey: ['integrations', activeProject],
    queryFn: async () => (await api.get(base)).data.data as ConfiguredIntegration[],
  });

  const entitlements = useQuery({
    enabled: !!orgId,
    queryKey: ['entitlements', orgId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/billing/entitlements`)).data as {
        code: string;
        integrationsEnabled: boolean;
        apmEnabled: boolean;
      },
  });
  const ent = entitlements.data;

  const byProvider = new Map<string, ConfiguredIntegration[]>();
  for (const i of integrations.data ?? []) {
    byProvider.set(i.provider, [...(byProvider.get(i.provider) ?? []), i]);
  }

  // Which plan (if any) a provider needs, given the org's current entitlements.
  const lockedBy = (spec: ProviderSpec): 'Pro' | 'Scale' | null => {
    if (!ent) return null; // don't flash a lock before entitlements load
    if (!ent.integrationsEnabled) return 'Pro';
    if (spec.apm && !ent.apmEnabled) return 'Scale';
    return null;
  };

  const save = useMutation({
    mutationFn: async (vars: { provider: string; label: string; config: Record<string, string>; enabled: boolean }) =>
      (
        await api.put(`${base}/${vars.provider}?label=${encodeURIComponent(vars.label)}`, {
          config: vars.config,
          enabled: vars.enabled,
        })
      ).data as { pairing?: { paired: boolean; reason?: string } | null },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ['integrations', activeProject] });
      setEditing(null);
      // Pairing is the half nobody sees. Saving the token makes the Backend
      // tab work whether or not CoverOps learned our address, so the only way
      // to know the other direction took is to say so here.
      const pairing = data?.pairing;
      if (pairing) {
        setTestResult({
          key: `${vars.provider}:${vars.label}`,
          ok: pairing.paired,
          msg: pairing.paired
            ? 'Paired ✓. CoverOps can now link back to these sessions'
            : (PAIRING_TROUBLE[pairing.reason ?? ''] ??
              'Saved. CoverOps did not record where we are; set the session link there by hand.'),
        });
      }
    },
  });

  const toggle = useMutation({
    mutationFn: async (vars: { provider: string; label: string; enabled: boolean; config: Record<string, unknown> }) =>
      api.put(`${base}/${vars.provider}?label=${encodeURIComponent(vars.label)}`, {
        config: vars.config,
        enabled: vars.enabled,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', activeProject] }),
  });

  const remove = useMutation({
    mutationFn: async (v: { provider: string; label: string }) =>
      api.delete(`${base}/${v.provider}?label=${encodeURIComponent(v.label)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations', activeProject] }),
  });

  const test = useMutation({
    mutationFn: async (v: { provider: string; label: string }) =>
      (await api.post(`${base}/${v.provider}/test?label=${encodeURIComponent(v.label)}`)).data,
    onSuccess: (data, v) =>
      setTestResult({
        key: `${v.provider}:${v.label}`,
        ok: !!data.ok,
        msg: data.ok ? 'Connection verified ✓' : `Failed: ${data.error ?? 'unknown error'}`,
      }),
    onError: (_e, v) => setTestResult({ key: `${v.provider}:${v.label}`, ok: false, msg: 'Request failed' }),
  });

  const openConfigure = (spec: ProviderSpec, profile?: ConfiguredIntegration) => {
    setEditing(spec);
    setEditingLabel(profile?.label ?? nextLabel(byProvider.get(spec.key) ?? []));
    setEditingIsNew(!profile);
    // Editing starts from what is stored: hosts and keys come back readable,
    // secrets come back as their redaction. Saving a still-masked secret keeps
    // the stored one — the server treats the mask as "unchanged".
    const cfg = profile?.config ?? {};
    setForm(Object.fromEntries(Object.entries(cfg).map(([k, v]) => [k, String(v ?? '')])));
  };

  const renderCard = (spec: ProviderSpec) => {
    const profiles = byProvider.get(spec.key) ?? [];
    const connected = profiles.length > 0;
    const locked = lockedBy(spec);
    return (
      <Grid item xs={12} sm={6} md={4} key={spec.key}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: locked ? 0.85 : 1 }}>
          <CardContent sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ display: 'flex', filter: locked ? 'grayscale(1)' : 'none', opacity: locked ? 0.6 : 1 }}>
                <BrandIcon id={spec.key} name={spec.name} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{spec.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {spec.desc}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
              {locked ? (
                <Chip size="small" color="warning" icon={<LockIcon sx={{ fontSize: 14 }} />} label={`${locked} plan`} />
              ) : !connected ? (
                <Chip size="small" variant="outlined" label="Not configured" />
              ) : null}
              {spec.caps.includes('issue') && <Chip size="small" variant="outlined" label="issues" />}
              {spec.caps.includes('link') && <Chip size="small" variant="outlined" label="deep link" />}
              {spec.caps.includes('forward') && <Chip size="small" variant="outlined" label="forward" />}
              <Chip size="small" variant="outlined" label={MECHANISM_LABEL[spec.mechanism]} />
            </Stack>

            {/* One row per profile: a provider is no longer one connection. */}
            {!locked &&
              profiles.map((p) => {
                const result = testResult?.key === `${spec.key}:${p.label}` ? testResult : null;
                return (
                  <Box key={p.id} sx={{ mt: 1.5, p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        size="small"
                        color={p.enabled ? 'success' : 'default'}
                        label={p.label}
                        sx={{ fontWeight: 700 }}
                      />
                      <Box sx={{ flex: 1 }} />
                      <Switch
                        size="small"
                        checked={p.enabled}
                        onChange={(e) =>
                          toggle.mutate({ provider: spec.key, label: p.label, enabled: e.target.checked, config: p.config })
                        }
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Button size="small" startIcon={<SettingsIcon />} onClick={() => openConfigure(spec, p)}>
                        Edit
                      </Button>
                      {(spec.caps.includes('notify') || spec.caps.includes('issue')) && (
                        <Button
                          size="small"
                          startIcon={
                            test.isPending && test.variables?.provider === spec.key && test.variables?.label === p.label ? (
                              <CircularProgress size={14} />
                            ) : (
                              <ScienceIcon />
                            )
                          }
                          onClick={() => test.mutate({ provider: spec.key, label: p.label })}
                        >
                          Test
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => remove.mutate({ provider: spec.key, label: p.label })}
                      >
                        Remove
                      </Button>
                    </Stack>
                    {result && (
                      <Alert severity={result.ok ? 'success' : 'error'} sx={{ mt: 1, py: 0 }}>
                        {result.msg}
                      </Alert>
                    )}
                  </Box>
                );
              })}
          </CardContent>

          <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1 }}>
            {locked ? (
              <Button size="small" variant="outlined" color="warning" startIcon={<LockIcon />} onClick={() => navigate('/billing')}>
                Upgrade to {locked}
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                {/*
                  One button instead of "go to the vendor, make an API token,
                  copy it, come back, paste it, and also paste your account
                  email". Which is the sequence where integrations die, since
                  the person doing it rarely has the vendor admin rights.
                */}
                {spec.oauth && oauthAvailable.includes(spec.key) && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<LinkIcon />}
                    disabled={connecting === spec.key}
                    onClick={() => startConnect(spec.key)}
                  >
                    {connecting === spec.key ? 'Opening…' : connected ? 'Reconnect' : `Connect ${spec.name}`}
                  </Button>
                )}
                <Button size="small" startIcon={<SettingsIcon />} onClick={() => openConfigure(spec)}>
                  {connected ? 'Add profile' : 'Configure'}
                </Button>
              </Stack>
            )}
          </Box>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5">{c.title}</Typography>
        <TextField
          select
          size="small"
          label={c.project}
          value={activeProject}
          onChange={(e) => setProjectId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {projects.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {projects && projects.length === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/projects')}>
              Create a project
            </Button>
          }
        >
          Integrations are configured per project, and this organization doesn't have one yet.
          Create your first project, then come back here to connect your tools.
        </Alert>
      )}

      {ent && !ent.integrationsEnabled && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/billing')}>
              {c.viewPlans}
            </Button>
          }
        >
          {c.gateLead} <b>{ent.code}</b> {c.gateTail}
        </Alert>
      )}

      {projects && projects.length === 0
        ? null
        : CATEGORY_ORDER.map(({ name, desc }) => {
        const specs = PROVIDERS.filter((p) => p.category === name);
        if (specs.length === 0) return null;
        return (
          <Box key={name} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {desc}
            </Typography>
            <Grid container spacing={2}>
              {specs.map(renderCard)}
            </Grid>
          </Box>
        );
      })}

      {/* Configure dialog */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {editing && <BrandIcon id={editing.key} name={editing.name} size={24} />}
            <span>Configure {editing?.name}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {editing?.desc}. Secrets are stored server-side and shown redacted afterwards.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label={c.profileName}
            value={editingLabel}
            onChange={(e) => setEditingLabel(e.target.value)}
            disabled={!editingIsNew}
            helperText={
              editingIsNew
                ? c.profileNewHelp
                : c.profileLockedHelp
            }
          />
          {editing?.fields.map((f) => (
            <IntegrationField
              key={f.name}
              field={f}
              provider={editing.key}
              form={form}
              setForm={setForm}
              base={base}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>{common.cancel}</Button>
          <Button
            variant="contained"
            disabled={
              save.isPending ||
              !editing ||
              editing.fields.some((f) => !f.optional && !(form[f.name] ?? '').trim())
            }
            onClick={() =>
              editing &&
              save.mutate({
                provider: editing.key,
                label: editingLabel.trim() || 'default',
                config: prune(form),
                enabled: true,
              })
            }
          >
            {save.isPending ? 'Saving…' : 'Save & connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

